// components/RecipeCards.tsx

import React from "react";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  useTheme,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { createRecipeStyles } from "./styles";
import { getStaticAssetPath } from "../../utils/staticAssets";
import { FetchedRecipe } from "../../types/recipe";
import { ROUTES } from "../../constants/routes";

/**
 * Props for RecipeCards component.
 * @property {FetchedRecipe[]} recipes - Array of recipe objects to display.
 * @property {boolean} [editable] - Optional flag for editable mode (currently unused).
 */
interface RecipeCardsProps {
  recipes: FetchedRecipe[];
  editable?: boolean;
}

/**
 * RecipeCards Component
 *
 * Renders a responsive grid of recipe cards using Flexbox layout.
 * Each card displays a recipe's cover image and name, and acts as a
 * clickable link to navigate to the full recipe details page.
 *
 * Features:
 * - Responsive grid layout that adapts to screen size
 * - Hover effects and smooth transitions
 * - Accessible navigation with proper link semantics
 * - Graceful handling of missing data
 *
 * @param {RecipeCardsProps} props - Component props
 * @returns {JSX.Element} The rendered grid of recipe cards or empty state
 */
const RecipeCards: React.FC<RecipeCardsProps> = ({ recipes }) => {
  // Get theme for consistent styling across the app
  const theme = useTheme();
  const styles = createRecipeStyles(theme);

  // If no recipes, show a friendly empty state message
  if (!recipes || recipes.length === 0) {
    return (
      <Box sx={styles.cards.noRecipesContainer}>
        <Typography variant="body1" color="text.secondary">
          No recipes to display.
        </Typography>
      </Box>
    );
  }

  return (
    // Main container using flexbox for responsive grid layout
    <Box sx={styles.cards.cardContainer}>
      {recipes.map((recipe) =>
        recipe && recipe._id ? (
          <Box
            sx={{
              flex: "0 0 auto",
              width: { xs: 160, sm: 180, md: 200, lg: 220 },
              height: { xs: 260, sm: 280, md: 300, lg: 320 },
            }}
            key={recipe._id}
          >
            <MuiLink
              component={RouterLink}
              to={`${ROUTES.RECIPE_DETAIL}/${recipe._id}`}
              sx={{ textDecoration: "none" }}
            >
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  bgcolor: "grey.50",
                  transition: "border-color 0.2s",
                  borderColor: "transparent",
                  "&:hover": {
                    borderColor: theme.palette.materiom.seaweed,
                  },
                }}
              >
                <CardMedia
                  component="img"
                  image={
                    recipe.data &&
                    recipe.data.coverImage &&
                    typeof recipe.data.coverImage.source === "string" &&
                    recipe.data.coverImage.source.length > 0
                      ? getStaticAssetPath(recipe.data.coverImage.source)
                      : "/static/img/placeholder.png"
                  }
                  alt={
                    recipe.data &&
                    typeof recipe.data.name === "string" &&
                    recipe.data.name.length > 0
                      ? recipe.data.name
                      : "Recipe"
                  }
                  sx={{
                    height: { xs: 60, sm: 70, md: 80, lg: 90 },
                    width: "auto",
                    objectFit: "contain",
                    bgcolor: "grey.50",
                    mt: 0,
                    mb: 0,
                    display: "block",
                    mx: "auto",
                  }}
                />
                <CardContent
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    p: 2,
                    minHeight: 60,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: theme.palette.materiom.seaweed,
                      textAlign: "left",
                      fontWeight: 600,
                      wordBreak: "break-word",
                      fontSize: { xs: 16, sm: 17, md: 18, lg: 19 },
                      lineHeight: 1.2,
                      overflowWrap: "break-word",
                    }}
                    title={
                      recipe.data &&
                      typeof recipe.data.name === "string" &&
                      recipe.data.name.length > 0
                        ? recipe.data.name
                        : "Untitled Recipe"
                    }
                  >
                    {recipe.data &&
                    typeof recipe.data.name === "string" &&
                    recipe.data.name.length > 0
                      ? recipe.data.name
                      : "Untitled Recipe"}
                  </Typography>
                </CardContent>
              </Card>
            </MuiLink>
          </Box>
        ) : null
      )}
    </Box>
  );
};

export default RecipeCards;
